import { getEmValueFromStringThemeUtil } from 'modules/styles'
import {
  isLargeScreenViewportSelect,
  isMobileScreenViewportSelect,
  isVeryLargeScreenViewportSelect
} from 'modules/viewport/selectors'
import PropTypes from 'prop-types'
import React from 'react'
import getStyleComponentUtil from 'modules/component/utils/get-style'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import './index.scss'
import {
  arePropsEqualComponentUtil,
  logUtil
} from 'packages/src'
import {
  IMAGE_ASPECT_RATIO_ID,
  IMAGE_ASPECT_RATIO_ID_TO_SUFFIX,
  IMAGE_ASPECT_RATIO_ID_TO_SUFFIX_MOBILE_SCREEN,
  IMAGE_ASPECT_RATIO_ID_TO_SUFFIX_MOBILE_SCREEN_FOR_BOT,
  IMAGE_ASPECT_RATIO_ID_TO_SUFFIX_VERY_LARGE_SCREEN,
  IMAGE_ASPECT_RATIO_ID_TO_VALUE,
  IMAGE_SUFFIX
} from 'components/Image/constants'
import isBase64SrcImageUtil from 'components/Image/utils/is-base-64'
import isExternalSrcImageUtil from 'components/Image/utils/is-external-src'
import getUrlImageUtil from 'components/Image/utils/get-url'
import getExtensionImageComponentUtil from 'components/Image/utils/get-extension'
import isSvgSrcImageUtil from 'components/Image/utils/is-svg'
import getHeightFromWidthImageUtil from 'components/Image/utils/get-height-from-width'
import getPxValueFromStringImageUtil from 'components/Image/utils/get-px-value-from-string'
import hasWebpSupportAppSelect from 'modules/training/modules/app/selectors/has-webp-support'
import isFirstRenderAppSelect from 'modules/app/selectors/is-first-render/index.js'
import { ImageWebP } from 'components/Image/components/WebP/index.js'
import { ImageSource } from 'components/Image/components/Source/index.js'
import { ImageImg } from 'components/Image/components/Img/index.js'
import { ImageRawImg } from 'components/Image/components/RawImg/index.js'
import { isBotUserAgentUtil } from 'modules/user-agent/utils/is-bot/index.js'
import getUserAgentAppSelect from 'modules/training/modules/app/selectors/get-user-agent/index.js'

/**
 * @param {ImageProps} props
 * @returns {Element}
 * @constructor
 */
export const Image = React.memo(function Image ({
  aspectRatio = IMAGE_ASPECT_RATIO_ID.TALL,
  style: propStyle = {},
  containerStyle = {},
  src,
  alt,
  shouldPreload,
  onError,
  imgStyle,
  shouldLazyLoad,
  ...rest
}) {
  let {
    suffix,
    hasNoAspectRatio,
    shouldDirectlyLoadTheSrc
  } = rest

  const [shouldIgnoreWebpSupport, setShouldIgnoreWebpSupport] = React.useState(false)

  const isExternalSrc = isExternalSrcImageUtil(src)
  const isBase64 = isBase64SrcImageUtil(src)
  const isSvg = isSvgSrcImageUtil(src)
  const isFirstRender = useSelector(isFirstRenderAppSelect)
  const isVeryLargeScreen = useSelector(isVeryLargeScreenViewportSelect)
  const wasMountedOnServer = isFirstRender
  // const wasMountedOnServer = useSelector(
  //   wasMountedOnServerImageSelector(src)
  // )
  const hasWebpSupport = useSelector(hasWebpSupportAppSelect)
  const [error, setError] = React.useState()
  const isMobileScreen = useSelector(isMobileScreenViewportSelect)
  const isLargeScreen = useSelector(isLargeScreenViewportSelect)
  const extension = getExtensionImageComponentUtil(src)
  const fullSrc = getUrlImageUtil(src)
  const srcWithoutExtension = fullSrc.replace(extension, '')
  const userAgent = useSelector(getUserAgentAppSelect)
  const isBot = isBotUserAgentUtil(userAgent)

  const style = getStyleComponentUtil({
    borderRadius: '0.27em',
    overflow: 'hidden',
    width: '100%'
  }, propStyle)

  const { height } = style
  const width = rest.width || style.width
  if (height && width) {
    hasNoAspectRatio = true
  }

  // const debugProps = {
  //   props,
  //   style,
  //   height,
  //   width,
  //   isExternalSrc,
  //   isBase64,
  //   isSvg,
  //   fullSrc,
  //   wasMountedOnServer,
  //   hasWebpSupport,
  //   hasNoAspectRatio
  // }

  // logUtil('Image').debug('debugProps=', debugProps)
  // if (SERVER) {
  //   const pathname = getPathnameImageUtil(src)
  //   // logUtil('Image#SERVER').debug('wasMountedOnServer pathname=', pathname)
  //   dispatch(
  //     setStateComponentAction(
  //       IMAGE_COMPONENT_NAME,
  //       pathname,
  //       {
  //         wasMountedOnServer: true,
  //         src
  //       }
  //     )
  //   )
  // }

  shouldDirectlyLoadTheSrc = (
    shouldDirectlyLoadTheSrc ||
    isExternalSrc ||
    isBase64 ||
    isSvg
  )
  const shouldNotHaveSmoothBlurAppear = (
    shouldDirectlyLoadTheSrc ||
    hasNoAspectRatio
  )
  let className = shouldNotHaveSmoothBlurAppear
    ? ''
    : 'mediabox-img'
  if (shouldPreload) {
    className += ' lazypreload'
  }

  let isLazyLoading = false
  if (
    // If the image was mounted on the server, ignore `lazyload`
    // which means progressive images won't work
    // ignore when `shouldLazyLoad` is explicitly set
    (
      !wasMountedOnServer &&
      shouldLazyLoad == null
    ) ||
    shouldLazyLoad
  ) {
    isLazyLoading = true
    className += ' lazyload blur-up'
  }

  React.useEffect(() => {
    setError()
  }, [src])

  const logPrefix = `src=${src}`

  const handleError = (err) => {
    logUtil('Image#handleError').error(`${logPrefix} err=`, err)
    logUtil('Image#handleError').error(`${logPrefix} err?.currentTarget?.currentSrc=`, err?.currentTarget?.currentSrc)

    if (
      !shouldIgnoreWebpSupport &&
      hasWebpSupport
    ) {
      logUtil('Image#handleError').debug(`${logPrefix} try to load the non-webp image`)
      setShouldIgnoreWebpSupport(true)
      return
    }

    if (error) {
      logUtil('Image#handleError').debug(`${logPrefix} [noop] already has error`)
      return
    }

    if (!onError) {
      setError(err)
      return
    }

    logUtil('Image#handleError').debug(`${logPrefix} call onError`)
    onError?.(err)
  }

  if (shouldDirectlyLoadTheSrc) {
    return (
      <ImageRawImg
        wasMountedOnServer={wasMountedOnServer}
        onError={handleError}
        src={src}
        style={style}
        className={className}
        alt={alt}
      />
    )
  }

  const {
    ignoreDimensions
  } = rest

  let aspectRatioMapToUse
  if (isMobileScreen && isBot) {
    aspectRatioMapToUse = IMAGE_ASPECT_RATIO_ID_TO_SUFFIX_MOBILE_SCREEN_FOR_BOT
  } else if (isMobileScreen) {
    aspectRatioMapToUse = IMAGE_ASPECT_RATIO_ID_TO_SUFFIX_MOBILE_SCREEN
  } else if (isVeryLargeScreen) {
    aspectRatioMapToUse = IMAGE_ASPECT_RATIO_ID_TO_SUFFIX_VERY_LARGE_SCREEN
  } else {
    aspectRatioMapToUse = IMAGE_ASPECT_RATIO_ID_TO_SUFFIX
  }

  if (
    !suffix &&
    !ignoreDimensions &&
    !hasNoAspectRatio
  ) {
    suffix = (
      aspectRatioMapToUse[aspectRatio] ||
      aspectRatioMapToUse[IMAGE_ASPECT_RATIO_ID.WIDE]
    )
  }

  if (
    !suffix &&
    !ignoreDimensions &&
    isMobileScreen
  ) {
    suffix = IMAGE_SUFFIX.SMALL
  } else if (!suffix && !ignoreDimensions && !isLargeScreen) {
    suffix = IMAGE_SUFFIX.MEDIUM
  }

  let srcSet
  if (suffix === IMAGE_SUFFIX.TINY) {
    srcSet = [
      `${srcWithoutExtension}-tiny${extension} 220w`
    ]
  } else if (suffix === IMAGE_SUFFIX.SMALL) {
    srcSet = [
      `${srcWithoutExtension}-small${extension} 300w`
    ]
  } else if (suffix === IMAGE_SUFFIX.MEDIUM) {
    srcSet = [
      `${srcWithoutExtension}-medium${extension} 600w`
    ]
  } else {
    srcSet = [
      `${srcWithoutExtension}-large${extension} 900w`
    ]
  }

  // const srcSet = [
  //   `${srcWithoutExtension}-tiny${extension} 220w`,
  //   `${srcWithoutExtension}-small${extension} 300w`,
  //   `${srcWithoutExtension}-medium${extension} 600w`,
  //   `${srcWithoutExtension}-large${extension} 900w`
  // ]
  const srcSetCount = srcSet.length

  let wrapperStyle = {
    position: 'relative',
    display: 'block',
    height: '0',
    width: '100%'
  }

  let widthValue
  let heightValueSuffix
  if (
    !height &&
    !hasNoAspectRatio &&
    width &&
    width.includes('em')
  ) {
    widthValue = getEmValueFromStringThemeUtil(width)
    heightValueSuffix = 'em'
  } else if (
    !height &&
    !hasNoAspectRatio &&
    width &&
    width.includes('px')
  ) {
    widthValue = getPxValueFromStringImageUtil(width)
    heightValueSuffix = 'px'
  }

  const heightFromAspectRatio = getHeightFromWidthImageUtil(widthValue, aspectRatio)
  if (height) {
    wrapperStyle.height = height
  } else if (heightFromAspectRatio) {
    wrapperStyle.height = `${heightFromAspectRatio}${heightValueSuffix}`
  }

  if (!hasNoAspectRatio) {
    const ratio = IMAGE_ASPECT_RATIO_ID_TO_VALUE[aspectRatio]
    wrapperStyle.paddingBottom = widthValue ? undefined : `${ratio}%`
  }

  // 1920 x 1080 -> height 1080px
  wrapperStyle = getStyleComponentUtil(wrapperStyle, rest.style)
  const isWebpEnabled = hasWebpSupport && !error

  const propsToPass = {
    extension,
    wasMountedOnServer,
    srcSet,
    isWebpEnabled,
    isLazyLoading
  }

  const debugProps = {
    rest,
    style,
    height,
    width,
    isExternalSrc,
    isBase64,
    isSvg,
    fullSrc,
    hasWebpSupport,
    hasNoAspectRatio,
    aspectRatio,
    suffix,
    extension,
    srcSetCount,
    propsToPass
  }
  logUtil('Image').debug(`${logPrefix} debugProps=`, debugProps)

  return (
    <div style={getStyleComponentUtil(style, containerStyle)}>
      <div style={wrapperStyle}>
        <picture>
          {
            !shouldIgnoreWebpSupport && (
              <ImageWebP
                {...propsToPass}
              />
            )
          }

          <ImageSource
            {...propsToPass}
            extension={extension}
          />

          <ImageImg
            {...propsToPass}
            srcSetCount={srcSetCount}
            alt={alt}
            imgStyle={imgStyle}
            className={className}
            onError={handleError}
          />
        </picture>
      </div>
    </div>
  )
}, arePropsEqualComponentUtil({
  keysToIgnore: ['onError']
}))

Image.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  aspectRatio: PropTypes.oneOf(Object.values(IMAGE_ASPECT_RATIO_ID)),
  width: PropTypes.string,
  isVeryTall: PropTypes.bool,
  hasNoAspectRatio: PropTypes.bool,
  style: PropTypes.object,
  shouldPreload: PropTypes.bool,
  hasWebpSupport: PropTypes.bool
}

Image.ASPECT_RATIO = IMAGE_ASPECT_RATIO_ID
